import {
    ContentCopy,
    Delete,
    Edit,
    PeopleAlt,
    PushPin,
    PushPinOutlined,
} from '@mui/icons-material'
import {
    alpha,
    Box,
    Button,
    Chip,
    Divider,
    Rating,
    Tooltip,
    Typography,
} from '@mui/material'
import { CandidateGroupNotes, CandidateNote } from '__generated__/graphql'
import StarterKit from '@tiptap/starter-kit'
import { formatDate } from 'date-fns'
import startCase from 'lodash.startcase'
import { RichTextReadOnly } from 'mui-tiptap'
import { Link } from 'react-router-dom'
import type { CandidateNote as CandidateNoteType } from 'recruiter/graphql/candidateNotes.gql'
import { recruiterRouteConfig } from 'recruiter/routes'
import { getFullName } from 'shared/utils'

interface NoteDetailPanelProps {
    note: CandidateNoteType | null
    onPin: () => void
    onEdit: () => void
    onDelete?: () => void
    onCopy: () => void
}

const getNoteCompanyAndRoster = (note: CandidateNoteType) => {
    if (note.noteType === 'group') {
        const groupNote = note.note as CandidateGroupNotes
        return {
            company: groupNote?.groupNote?.company,
            roster: groupNote?.groupNote?.roster,
        }
    }
    const singleNote = note.note as CandidateNote
    return {
        company: singleNote?.company,
        roster: singleNote?.roster,
    }
}

const NoteDetailPanel = ({
    note,
    onPin,
    onEdit,
    onDelete,
    onCopy,
}: NoteDetailPanelProps) => {
    if (!note) {
        return (
            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Typography variant="body1" color="text.secondary">
                    Select a note to view
                </Typography>
            </Box>
        )
    }

    const author = note.createdByUser || note.updatedByUser
    const authorName = author ? getFullName(author) : 'Unknown User'
    const createdStr = formatDate(
        new Date(note.createdAt || note.updatedAt),
        'MMM d yyyy'
    )
    const updatedStr = formatDate(
        new Date(note.updatedAt || note.createdAt),
        'MMM d yyyy'
    )
    const isPinningDisabled = note.template?.templateType === 'Main'
    const { company, roster } = getNoteCompanyAndRoster(note)

    return (
        <Box
            sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minWidth: 0,
                overflow: 'hidden',
            }}
        >
            {/* Header */}
            <Box sx={{ px: 2.5, py: 2, flexShrink: 0 }}>
                {/* Line 1: Template · Author */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body1" fontWeight="bold">
                        {startCase(note.template?.templateType)}
                    </Typography>
                    {note.noteType === 'group' && (
                        <PeopleAlt
                            sx={{ fontSize: 16, color: 'text.secondary' }}
                        />
                    )}
                    {note.isPinned && (
                        <PushPin
                            sx={{ fontSize: 16, color: 'text.secondary' }}
                        />
                    )}
                    <Typography variant="body1" color="text.secondary">
                        &middot;
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {authorName}
                    </Typography>
                </Box>

                {/* Line 2: Created · Updated */}
                <Typography variant="caption" color="text.secondary">
                    Created {createdStr} &middot; Updated {updatedStr}
                </Typography>

                {note.starRating !== null &&
                    note.starRating !== undefined &&
                    note.starRating > 0 && (
                        <Tooltip
                            title={note.excludeFromEvaluation ? 'Remeet' : ''}
                            arrow
                        >
                            <Box
                                component="span"
                                sx={{ display: 'inline-flex' }}
                            >
                                <Rating
                                    size="small"
                                    precision={0.5}
                                    value={note.starRating}
                                    readOnly
                                    sx={{
                                        mt: 0.5,
                                        transform: 'translateY(3px)',
                                        ml: 0.5,
                                        ...(note.excludeFromEvaluation && {
                                            filter: 'grayscale(1) brightness(0.85)',
                                        }),
                                    }}
                                />
                            </Box>
                        </Tooltip>
                    )}

                {note.excludeFromEvaluation && (
                    <Chip
                        label="Remeet"
                        size="small"
                        sx={{ ml: 0.5, height: 18, fontSize: '0.65rem' }}
                    />
                )}

                {(company?.id || roster?.id) && (
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 1,
                            flexWrap: 'wrap',
                            mt: 1,
                        }}
                    >
                        {company?.id && (
                            <Chip
                                component={Link}
                                to={`${recruiterRouteConfig.companies.path}/${company.id}`}
                                label={company.value}
                                size="small"
                                clickable
                                onClick={(e) => e.stopPropagation()}
                                sx={{
                                    textDecoration: 'none',
                                    bgcolor: (theme) =>
                                        alpha(theme.palette.primary.main, 0.08),
                                    color: 'primary.dark',
                                    '&:hover': {
                                        bgcolor: (theme) =>
                                            alpha(
                                                theme.palette.primary.main,
                                                0.16
                                            ),
                                    },
                                }}
                            />
                        )}
                        {roster?.id && (
                            <Chip
                                component={Link}
                                to={`${recruiterRouteConfig.rosters.path}/${roster.id}`}
                                label={roster.rosterName}
                                size="small"
                                clickable
                                onClick={(e) => e.stopPropagation()}
                                sx={{
                                    textDecoration: 'none',
                                    bgcolor: (theme) =>
                                        alpha(theme.palette.primary.main, 0.08),
                                    color: 'primary.dark',
                                    '&:hover': {
                                        bgcolor: (theme) =>
                                            alpha(
                                                theme.palette.primary.main,
                                                0.16
                                            ),
                                    },
                                }}
                            />
                        )}
                    </Box>
                )}
            </Box>

            <Divider />

            {/* Content */}
            <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 2 }}>
                <Typography
                    component="div"
                    variant="body2"
                    color="text.primary"
                    sx={{ lineHeight: 1.6 }}
                >
                    <RichTextReadOnly
                        extensions={[StarterKit]}
                        content={note.noteData}
                    />
                </Typography>
            </Box>

            {/* Actions */}
            <Divider />
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 2,
                    py: 1,
                    flexShrink: 0,
                }}
            >
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Edit />}
                    onClick={onEdit}
                >
                    Edit
                </Button>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={
                        note.isPinned ? <PushPin /> : <PushPinOutlined />
                    }
                    onClick={onPin}
                    disabled={isPinningDisabled}
                >
                    {note.isPinned ? 'Unpin' : 'Pin'}
                </Button>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ContentCopy />}
                    onClick={onCopy}
                >
                    Copy
                </Button>
                {onDelete && (
                    <Button
                        variant="outlined"
                        size="small"
                        color="error"
                        startIcon={<Delete />}
                        onClick={onDelete}
                        sx={{ ml: 'auto' }}
                    >
                        Delete
                    </Button>
                )}
            </Box>
        </Box>
    )
}

export default NoteDetailPanel
